import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useApi } from '../../hooks/useApi';
import { useAppState } from '../../context/AppStateContext';
import FilterDropdown from '../common/FilterDropdown';
import {
    CalendarDaysIcon,
    ChevronDownIcon,
    ChevronUpIcon,
    PlusIcon,
    PencilSquareIcon,
    TrashIcon,
    XMarkIcon
} from '@heroicons/react/24/outline';

const AcademicCycleModal = ({ onClose }) => {
    const { get, post, put, del } = useApi();
    const { showToast } = useAppState();
    const [activeTab, setActiveTab] = useState('add'); // 'add' or 'list'
    const [years, setYears] = useState([]);
    const [terms, setTerms] = useState([]);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);

    const [mode, setMode] = useState('term'); // 'year' or 'term'
    const [editingItem, setEditingItem] = useState(null);
    const [expandedYearIds, setExpandedYearIds] = useState({});

    const [yearData, setYearData] = useState({
        name: '',
        start_date: '',
        end_date: '',
        is_current: false
    });

    const [termData, setTermData] = useState({
        year: '',
        name: '',
        start_date: '',
        end_date: '',
        is_final_term: false,
        is_current: false
    });

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const [yearsData, termsData] = await Promise.all([
                get('/api/academic-years/'),
                get('/api/academic-terms/')
            ]);
            const fetchedYears = yearsData || [];
            const fetchedTerms = termsData || [];
            setYears(fetchedYears);
            setTerms(fetchedTerms);

            // Default expand current/active year
            const currentYear = fetchedYears.find(y => y.is_current) || fetchedYears[0];
            if (currentYear) {
                setExpandedYearIds(prev => ({ ...prev, [currentYear.id]: true }));
            }
        } catch (error) {
            console.error('Error fetching cycle data:', error);
            showToast('Failed to load academic cycles', 'error');
        } finally {
            setLoading(false);
        }
    }, [get, showToast]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const toggleYearExpand = (yearId) => {
        setExpandedYearIds(prev => ({
            ...prev,
            [yearId]: !prev[yearId]
        }));
    };

    const termsByYear = useMemo(() => {
        const map = {};
        (years || []).forEach(y => {
            map[y.id] = (terms || []).filter(t => String(t.year) === String(y.id));
        });
        return map;
    }, [years, terms]);

    const handleSubmitYear = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            if (editingItem) {
                await put(`/api/academic-years/${editingItem.id}/`, yearData);
                showToast('Academic year updated');
            } else {
                await post('/api/academic-years/', yearData);
                showToast('Academic year created');
            }
            fetchData();
            resetForm();
            setActiveTab('list');
        } catch (error) {
            showToast('Failed to save academic year', 'error');
        } finally {
            setSubmitting(false);
        }
    };

    const handleSubmitTerm = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            if (editingItem) {
                await put(`/api/academic-terms/${editingItem.id}/`, termData);
                showToast('Academic term updated');
            } else {
                await post('/api/academic-terms/', termData);
                showToast('Academic term created');
            }
            fetchData();
            resetForm();
            setActiveTab('list');
        } catch (error) {
            showToast('Failed to save academic term', 'error');
        } finally {
            setSubmitting(false);
        }
    };

    const handleEditYear = (year) => {
        setMode('year');
        setEditingItem(year);
        setYearData({
            name: year.name,
            start_date: year.start_date,
            end_date: year.end_date,
            is_current: year.is_current
        });
        setActiveTab('add');
    };

    const handleEditTerm = (term) => {
        setMode('term');
        setEditingItem(term);
        setTermData({
            year: term.year,
            name: term.name,
            start_date: term.start_date,
            end_date: term.end_date,
            is_final_term: term.is_final_term,
            is_current: term.is_current
        });
        setActiveTab('add');
    };

    const handleNewTermForYear = (yearId) => {
        resetForm();
        setMode('term');
        setTermData(prev => ({ ...prev, year: yearId }));
        setActiveTab('add');
    };

    const handleDeleteYear = async (id) => {
        if (!window.confirm('Delete this year? This may affect linked terms.')) return;
        try {
            await del(`/api/academic-years/${id}/`);
            showToast('Academic year deleted');
            fetchData();
        } catch (error) {
            showToast('Failed to delete', 'error');
        }
    };

    const handleDeleteTerm = async (id) => {
        if (!window.confirm('Delete this term?')) return;
        try {
            await del(`/api/academic-terms/${id}/`);
            showToast('Academic term deleted');
            fetchData();
        } catch (error) {
            showToast('Failed to delete', 'error');
        }
    };

    const resetForm = () => {
        setEditingItem(null);
        setYearData({ name: '', start_date: '', end_date: '', is_current: false });
        setTermData({ year: '', name: '', start_date: '', end_date: '', is_final_term: false, is_current: false });
    };

    const modeOptions = [
        { value: 'term', name: 'Termly Session' },
        { value: 'year', name: 'Academic Year' }
    ];

    return (
        <div className="fixed inset-0 bg-[#0F172A]/40 backdrop-blur-md z-50 flex items-center justify-center p-4 animate-in fade-in duration-300">
            <div className="bg-white rounded-[2.5rem] shadow-2xl w-full max-w-3xl overflow-hidden border border-white/20 animate-in zoom-in-95 duration-500 max-h-[90vh] flex flex-col">
                {/* Header */}
                <div className="p-8 pb-4 border-b border-slate-100 flex items-center justify-between flex-shrink-0">
                    <div>
                        <h2 className="text-2xl font-black text-[#18216D] tracking-tighter uppercase italic">
                            Academic Cycle Hub
                        </h2>
                        <p className="text-xs font-bold text-slate-500 uppercase tracking-widest mt-1">
                            Manage school years and termly sessions
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        className="w-10 h-10 rounded-full flex items-center justify-center text-slate-400 hover:text-rose-500 hover:bg-rose-50 transition-all"
                    >
                        <XMarkIcon className="w-6 h-6" />
                    </button>
                </div>

                {/* Tab Switcher */}
                <div className="px-8 pt-6 flex gap-8 flex-shrink-0 border-b border-slate-100 bg-slate-50/30">
                    <button
                        onClick={() => setActiveTab('add')}
                        className={`pb-4 text-xs font-black uppercase tracking-widest transition-all relative ${
                            activeTab === 'add' ? 'text-[#18216D]' : 'text-slate-400 hover:text-slate-600'
                        }`}
                    >
                        {editingItem ? 'Refine Configuration' : 'Establish Config'}
                        {activeTab === 'add' && (
                            <div className="absolute bottom-0 left-0 right-0 h-1 bg-[#FFC425] rounded-full"></div>
                        )}
                    </button>
                    <button
                        onClick={() => setActiveTab('list')}
                        className={`pb-4 text-xs font-black uppercase tracking-widest transition-all relative ${
                            activeTab === 'list' ? 'text-[#18216D]' : 'text-slate-400 hover:text-slate-600'
                        }`}
                    >
                        Active Cycles
                        {activeTab === 'list' && (
                            <div className="absolute bottom-0 left-0 right-0 h-1 bg-[#18216D] rounded-full"></div>
                        )}
                    </button>
                </div>

                {activeTab === 'add' ? (
                    <div className="flex-1 overflow-y-auto p-8 space-y-6">
                        {/* FilterDropdown Configuration Selector (Replaces flat pill buttons) */}
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-slate-50 p-4 rounded-3xl border border-slate-200/80">
                            <div>
                                <span className="text-[10px] font-black uppercase tracking-widest text-slate-400 block mb-0.5">
                                    Cycle Definition Type
                                </span>
                                <span className="text-sm font-black text-[#18216D] tracking-tight">
                                    {mode === 'term' ? 'Configuring Termly Session' : 'Configuring Academic Year'}
                                </span>
                            </div>
                            <div className="flex items-center gap-3">
                                <FilterDropdown
                                    value={mode}
                                    onChange={(newMode) => {
                                        if (newMode && newMode !== mode) {
                                            setMode(newMode);
                                            resetForm();
                                        }
                                    }}
                                    options={modeOptions}
                                    placeholder="Select Type"
                                    className="w-48"
                                />
                            </div>
                        </div>

                        {mode === 'year' ? (
                            <form onSubmit={handleSubmitYear} className="space-y-6">
                                <div className="space-y-1.5">
                                    <label className="text-xs font-black text-[#18216D] uppercase tracking-wider block">
                                        Academic Year Label
                                    </label>
                                    <input
                                        type="text"
                                        value={yearData.name}
                                        onChange={(e) => setYearData({ ...yearData, name: e.target.value })}
                                        className="w-full px-5 py-3.5 bg-white border-2 border-slate-200 hover:border-slate-300 focus:border-[#18216D] focus:ring-4 focus:ring-[#18216D]/10 rounded-2xl font-bold text-sm text-slate-900 transition-all shadow-xs"
                                        placeholder="e.g. 2026"
                                        required
                                    />
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                                    <div className="space-y-1.5">
                                        <label className="text-xs font-black text-[#18216D] uppercase tracking-wider block">
                                            Commencement (Start Date)
                                        </label>
                                        <input
                                            type="date"
                                            value={yearData.start_date}
                                            onChange={(e) => setYearData({ ...yearData, start_date: e.target.value })}
                                            className="w-full px-5 py-3.5 bg-white border-2 border-slate-200 hover:border-slate-300 focus:border-[#18216D] focus:ring-4 focus:ring-[#18216D]/10 rounded-2xl font-bold text-sm text-slate-900 transition-all shadow-xs"
                                            required
                                        />
                                    </div>
                                    <div className="space-y-1.5">
                                        <label className="text-xs font-black text-[#18216D] uppercase tracking-wider block">
                                            Conclusion (End Date)
                                        </label>
                                        <input
                                            type="date"
                                            value={yearData.end_date}
                                            onChange={(e) => setYearData({ ...yearData, end_date: e.target.value })}
                                            className="w-full px-5 py-3.5 bg-white border-2 border-slate-200 hover:border-slate-300 focus:border-[#18216D] focus:ring-4 focus:ring-[#18216D]/10 rounded-2xl font-bold text-sm text-slate-900 transition-all shadow-xs"
                                            required
                                        />
                                    </div>
                                </div>
                                <label className="flex items-center gap-3 bg-white p-4 rounded-2xl cursor-pointer border-2 border-slate-200 hover:border-indigo-200 transition-all w-fit shadow-xs">
                                    <input
                                        type="checkbox"
                                        checked={yearData.is_current}
                                        onChange={(e) => setYearData({ ...yearData, is_current: e.target.checked })}
                                        className="w-4 h-4 rounded border-slate-300 text-[#18216D] focus:ring-[#18216D]"
                                    />
                                    <div>
                                        <span className="text-xs font-black text-[#18216D] uppercase tracking-wider block">Active Academic Year</span>
                                        <span className="text-[10px] text-slate-400 font-bold">Designates this year as currently active in the school</span>
                                    </div>
                                </label>
                                <div className="pt-2 flex gap-4">
                                    <button
                                        type="submit"
                                        disabled={submitting}
                                        className="flex-1 px-10 py-4 bg-[#18216D] hover:bg-indigo-900 text-white rounded-2xl font-black text-[10px] uppercase tracking-[0.2em] transform hover:scale-[1.01] active:scale-98 transition-all shadow-xl shadow-indigo-900/10"
                                    >
                                        {submitting ? 'Processing...' : editingItem ? 'Update Year' : 'Publish Year'}
                                    </button>
                                    {editingItem && (
                                        <button
                                            type="button"
                                            onClick={resetForm}
                                            className="px-8 py-4 bg-slate-100 hover:bg-slate-200 text-slate-500 rounded-2xl font-black text-[10px] uppercase tracking-[0.2em] transition-all"
                                        >
                                            Dismiss
                                        </button>
                                    )}
                                </div>
                            </form>
                        ) : (
                            <form onSubmit={handleSubmitTerm} className="space-y-6">
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                                    <div className="space-y-1.5">
                                        <label className="text-xs font-black text-[#18216D] uppercase tracking-wider block">
                                            Parent Academic Year
                                        </label>
                                        <select
                                            value={termData.year}
                                            onChange={(e) => setTermData({ ...termData, year: e.target.value })}
                                            required
                                            className="w-full px-5 py-3.5 bg-white border-2 border-slate-200 hover:border-slate-300 focus:border-[#18216D] focus:ring-4 focus:ring-[#18216D]/10 rounded-2xl font-bold text-sm text-slate-900 transition-all shadow-xs"
                                        >
                                            <option value="">Select Academic Year</option>
                                            {years.map(y => (
                                                <option key={y.id} value={y.id}>
                                                    {y.name} {y.is_current ? '(Live)' : ''}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="space-y-1.5">
                                        <label className="text-xs font-black text-[#18216D] uppercase tracking-wider block">
                                            Term Name
                                        </label>
                                        <input
                                            type="text"
                                            value={termData.name}
                                            onChange={(e) => setTermData({ ...termData, name: e.target.value })}
                                            className="w-full px-5 py-3.5 bg-white border-2 border-slate-200 hover:border-slate-300 focus:border-[#18216D] focus:ring-4 focus:ring-[#18216D]/10 rounded-2xl font-bold text-sm text-slate-900 transition-all shadow-xs"
                                            placeholder="e.g. Term 1, Lent Term"
                                            required
                                        />
                                    </div>
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                                    <div className="space-y-1.5">
                                        <label className="text-xs font-black text-[#18216D] uppercase tracking-wider block">
                                            Term Start Date
                                        </label>
                                        <input
                                            type="date"
                                            value={termData.start_date}
                                            onChange={(e) => setTermData({ ...termData, start_date: e.target.value })}
                                            className="w-full px-5 py-3.5 bg-white border-2 border-slate-200 hover:border-slate-300 focus:border-[#18216D] focus:ring-4 focus:ring-[#18216D]/10 rounded-2xl font-bold text-sm text-slate-900 transition-all shadow-xs"
                                            required
                                        />
                                    </div>
                                    <div className="space-y-1.5">
                                        <label className="text-xs font-black text-[#18216D] uppercase tracking-wider block">
                                            Term End Date
                                        </label>
                                        <input
                                            type="date"
                                            value={termData.end_date}
                                            onChange={(e) => setTermData({ ...termData, end_date: e.target.value })}
                                            className="w-full px-5 py-3.5 bg-white border-2 border-slate-200 hover:border-slate-300 focus:border-[#18216D] focus:ring-4 focus:ring-[#18216D]/10 rounded-2xl font-bold text-sm text-slate-900 transition-all shadow-xs"
                                            required
                                        />
                                    </div>
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <label className="flex items-center gap-3 bg-white p-4 rounded-2xl cursor-pointer border-2 border-slate-200 hover:border-indigo-200 transition-all shadow-xs">
                                        <input
                                            type="checkbox"
                                            checked={termData.is_final_term}
                                            onChange={(e) => setTermData({ ...termData, is_final_term: e.target.checked })}
                                            className="w-4 h-4 rounded border-slate-300 text-[#18216D] focus:ring-[#18216D]"
                                        />
                                        <div>
                                            <span className="text-xs font-black text-[#18216D] uppercase tracking-wider block">Final Term</span>
                                            <span className="text-[10px] text-slate-400 font-bold">Marks conclusion of the academic year</span>
                                        </div>
                                    </label>
                                    <label className="flex items-center gap-3 bg-white p-4 rounded-2xl cursor-pointer border-2 border-slate-200 hover:border-emerald-200 transition-all shadow-xs">
                                        <input
                                            type="checkbox"
                                            checked={termData.is_current}
                                            onChange={(e) => setTermData({ ...termData, is_current: e.target.checked })}
                                            className="w-4 h-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                                        />
                                        <div>
                                            <span className="text-xs font-black text-emerald-700 uppercase tracking-wider block">Active / Live Term</span>
                                            <span className="text-[10px] text-slate-400 font-bold">Currently in progress across classes</span>
                                        </div>
                                    </label>
                                </div>
                                <div className="pt-2 flex gap-4">
                                    <button
                                        type="submit"
                                        disabled={submitting}
                                        className="flex-1 px-10 py-4 bg-[#18216D] hover:bg-indigo-900 text-white rounded-2xl font-black text-[10px] uppercase tracking-[0.2em] transform hover:scale-[1.01] active:scale-98 transition-all shadow-xl shadow-indigo-900/10"
                                    >
                                        {submitting ? 'Processing...' : editingItem ? 'Update Term' : 'Declare Term'}
                                    </button>
                                    {editingItem && (
                                        <button
                                            type="button"
                                            onClick={resetForm}
                                            className="px-8 py-4 bg-slate-100 hover:bg-slate-200 text-slate-500 rounded-2xl font-black text-[10px] uppercase tracking-[0.2em] transition-all"
                                        >
                                            Dismiss
                                        </button>
                                    )}
                                </div>
                            </form>
                        )}
                    </div>
                ) : (
                    /* Active Cycles Tab: Year-Enclosing Terms Accordion */
                    <div className="flex-1 overflow-y-auto p-8 bg-slate-50/40">
                        {loading ? (
                            <div className="py-20 text-center animate-pulse text-[10px] font-black text-slate-300 uppercase tracking-widest">
                                Querying Registries...
                            </div>
                        ) : years.length === 0 ? (
                            <div className="text-center py-16 bg-white rounded-3xl border border-slate-100 p-8 shadow-xs">
                                <CalendarDaysIcon className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                                <p className="text-sm font-black text-[#18216D] uppercase">No Academic Years Established</p>
                                <p className="text-xs text-slate-400 font-bold mt-1">Switch to 'Establish Config' to create your first academic year.</p>
                            </div>
                        ) : (
                            <div className="space-y-4">
                                {years.map(year => {
                                    const isExpanded = !!expandedYearIds[year.id];
                                    const yearTerms = termsByYear[year.id] || [];

                                    return (
                                        <div
                                            key={year.id}
                                            className={`bg-white rounded-3xl border transition-all overflow-hidden shadow-xs ${
                                                year.is_current ? 'border-indigo-200 ring-2 ring-indigo-500/10' : 'border-slate-100 hover:border-slate-200'
                                            }`}
                                        >
                                            {/* Year Header Card */}
                                            <div className="p-5 flex items-center justify-between gap-4 bg-slate-50/60">
                                                <button
                                                    type="button"
                                                    onClick={() => toggleYearExpand(year.id)}
                                                    className="flex-1 text-left flex items-center gap-3 group"
                                                >
                                                    <div className={`w-8 h-8 rounded-xl flex items-center justify-center transition-all ${
                                                        isExpanded ? 'bg-[#18216D] text-white' : 'bg-white border border-slate-200 text-slate-400 group-hover:border-[#18216D] group-hover:text-[#18216D]'
                                                    }`}>
                                                        {isExpanded ? (
                                                            <ChevronUpIcon className="w-4 h-4" />
                                                        ) : (
                                                            <ChevronDownIcon className="w-4 h-4" />
                                                        )}
                                                    </div>
                                                    <div>
                                                        <div className="flex items-center gap-2">
                                                            <span className="text-base font-black text-[#18216D] uppercase tracking-tight">
                                                                {year.name}
                                                            </span>
                                                            {year.is_current && (
                                                                <span className="bg-emerald-100 text-emerald-800 text-[9px] px-2.5 py-0.5 rounded-full font-black uppercase tracking-wider flex items-center gap-1 shadow-xs">
                                                                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
                                                                    Live Academic Year
                                                                </span>
                                                            )}
                                                            <span className="text-[10px] font-black text-slate-400 bg-white border border-slate-200 px-2 py-0.5 rounded-lg uppercase">
                                                                {yearTerms.length} {yearTerms.length === 1 ? 'Term' : 'Terms'}
                                                            </span>
                                                        </div>
                                                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mt-0.5">
                                                            {year.start_date} → {year.end_date}
                                                        </p>
                                                    </div>
                                                </button>

                                                {/* Actions on Year */}
                                                <div className="flex items-center gap-1.5 shrink-0">
                                                    <button
                                                        type="button"
                                                        onClick={() => handleNewTermForYear(year.id)}
                                                        className="hidden sm:inline-flex items-center gap-1 px-3 py-1.5 bg-indigo-50 hover:bg-[#18216D] text-[#18216D] hover:text-white rounded-xl text-[10px] font-black uppercase tracking-wider transition-all"
                                                        title="Add Term to this Year"
                                                    >
                                                        <PlusIcon className="w-3.5 h-3.5" />
                                                        Add Term
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleEditYear(year)}
                                                        className="w-8 h-8 rounded-xl bg-slate-100 hover:bg-[#18216D] text-slate-500 hover:text-white flex items-center justify-center transition-all shadow-xs"
                                                        title="Edit Year"
                                                    >
                                                        <PencilSquareIcon className="w-4 h-4" />
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleDeleteYear(year.id)}
                                                        className="w-8 h-8 rounded-xl bg-rose-50 hover:bg-rose-500 text-rose-500 hover:text-white flex items-center justify-center transition-all shadow-xs"
                                                        title="Delete Year"
                                                    >
                                                        <TrashIcon className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            </div>

                                            {/* Collapsible Enclosed Terms */}
                                            {isExpanded && (
                                                <div className="p-5 pt-3 border-t border-slate-100 bg-white space-y-2.5 animate-in slide-in-from-top-2 duration-200">
                                                    {yearTerms.length === 0 ? (
                                                        <div className="py-6 text-center border-2 border-dashed border-slate-100 rounded-2xl bg-slate-50/50">
                                                            <p className="text-xs font-bold text-slate-400">No terms declared for {year.name} yet.</p>
                                                            <button
                                                                type="button"
                                                                onClick={() => handleNewTermForYear(year.id)}
                                                                className="mt-2 inline-flex items-center gap-1 text-[10px] font-black text-indigo-600 hover:text-indigo-800 uppercase tracking-wider"
                                                            >
                                                                <PlusIcon className="w-3.5 h-3.5" />
                                                                Declare First Term
                                                            </button>
                                                        </div>
                                                    ) : (
                                                        yearTerms.map(term => (
                                                            <div
                                                                key={term.id}
                                                                className="p-3.5 bg-slate-50/70 hover:bg-slate-50 rounded-2xl border border-slate-100 flex items-center justify-between gap-3 group transition-colors"
                                                            >
                                                                <div className="flex items-center gap-3">
                                                                    <div className="w-8 h-8 rounded-xl bg-white border border-slate-200 flex items-center justify-center font-black text-[10px] text-[#18216D] shrink-0">
                                                                        {term.name.match(/\d+/) ? `T${term.name.match(/\d+/)[0]}` : 'T'}
                                                                    </div>
                                                                    <div>
                                                                        <div className="flex items-center gap-2">
                                                                            <span className="text-xs font-black text-[#18216D] uppercase tracking-tight">
                                                                                {term.name}
                                                                            </span>
                                                                            {term.is_current && (
                                                                                <span className="bg-emerald-100 text-emerald-800 text-[8px] font-black px-2 py-0.5 rounded-full uppercase tracking-wider">
                                                                                    Active Term
                                                                                </span>
                                                                            )}
                                                                            {term.is_final_term && (
                                                                                <span className="bg-purple-100 text-purple-800 text-[8px] font-black px-2 py-0.5 rounded-full uppercase tracking-wider">
                                                                                    Final Term
                                                                                </span>
                                                                            )}
                                                                        </div>
                                                                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mt-0.5">
                                                                            {term.start_date} → {term.end_date}
                                                                        </p>
                                                                    </div>
                                                                </div>

                                                                <div className="flex items-center gap-1.5 opacity-90 group-hover:opacity-100 transition-opacity">
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => handleEditTerm(term)}
                                                                        className="px-2.5 py-1 rounded-lg text-[9px] font-black uppercase tracking-wider bg-white border border-slate-200 text-[#18216D] hover:bg-indigo-50 hover:border-indigo-200 transition-all shadow-xs"
                                                                    >
                                                                        Refine
                                                                    </button>
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => handleDeleteTerm(term.id)}
                                                                        className="px-2.5 py-1 rounded-lg text-[9px] font-black uppercase tracking-wider bg-white border border-slate-200 text-rose-500 hover:bg-rose-50 hover:border-rose-200 transition-all shadow-xs"
                                                                    >
                                                                        Evict
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        ))
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default AcademicCycleModal;
